"use client";

import { createLogger } from "@/lib/observability/logger";
import {
  formatAddress,
  type GeolocationResult,
  type PlaceDetails,
} from "./place-details.shared";

const logger = createLogger("geocoding");

/**
 * Reverse geocode GPS coordinates to address details using Google Geocoding API
 * @param lat - Latitude
 * @param lng - Longitude
 * @returns PlaceDetails object or null if not found
 */
export async function reverseGeocode(
  lat: number,
  lng: number,
): Promise<PlaceDetails | null> {
  try {
    // Use the Geocoding API for reverse geocoding
    const geocoder = new google.maps.Geocoder();

    const response = await geocoder.geocode({
      location: { lat, lng },
    });

    if (response.results && response.results.length > 0) {
      const result = response.results[0];

      if (!result) {
        return null;
      }

      // Extract address components
      let city = "",
        state = "",
        country = "",
        postalCode = "",
        streetNumber = "",
        route = "",
        subpremise = "";

      result.address_components?.forEach((component) => {
        const types = component.types || [];

        if (types.includes("locality")) {
          city = component.long_name;
        } else if (types.includes("sublocality")) {
          city = component.long_name;
        } else if (types.includes("administrative_area_level_1")) {
          state = component.short_name || component.long_name;
        } else if (types.includes("country")) {
          country = component.short_name || component.long_name;
        } else if (types.includes("postal_code")) {
          postalCode = component.long_name;
        } else if (types.includes("street_number")) {
          streetNumber = component.long_name;
        } else if (types.includes("route")) {
          route = component.long_name;
        } else if (types.includes("subpremise")) {
          subpremise = component.long_name;
        }
      });

      const address1 = `${streetNumber} ${route}`.trim();
      const address2 = subpremise || null;
      const formattedAddress =
        result.formatted_address ||
        formatAddress(address1, address2, city, state, postalCode);

      // Create bounding box from geometry
      const bounds = result.geometry?.viewport;
      const boundingBox = bounds
        ? {
            northeast: {
              lat: bounds.getNorthEast().lat(),
              lng: bounds.getNorthEast().lng(),
            },
            southwest: {
              lat: bounds.getSouthWest().lat(),
              lng: bounds.getSouthWest().lng(),
            },
          }
        : {
            northeast: { lat: lat + 0.01, lng: lng + 0.01 },
            southwest: { lat: lat - 0.01, lng: lng - 0.01 },
          };

      return {
        address1,
        address2,
        boundingBox,
        center: { lat, lng },
        city,
        country,
        formattedAddress,
        name: null,
        placeId: result.place_id,
        plusCodeGlobal: result.plus_code?.global_code || null,
        postalCode,
        state,
      };
    }

    return null;
  } catch (error) {
    logger.logError("reverseGeocode", error as Error);
    return null;
  }
}

/**
 * Get current GPS location and reverse geocode it to address details
 * @returns GeolocationResult with coordinates and address
 * @throws Error if geolocation is not supported or permission denied
 */
export async function getCurrentLocation(): Promise<GeolocationResult> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      const error = new Error("Geolocation is not supported by this browser");
      logger.logError("getCurrentLocation", error);
      reject(error);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;

          // Reverse geocode the coordinates to get address details
          const address = await reverseGeocode(latitude, longitude);

          resolve({
            coords: { latitude, longitude },
            address,
          });
        } catch (error) {
          logger.logError("getCurrentLocation", error as Error);
          // Still return coordinates even if reverse geocoding fails
          resolve({
            coords: {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            },
            address: null,
          });
        }
      },
      (error) => {
        const errorObj = new Error(`Geolocation error: ${error.message}`);
        logger.logError("getCurrentLocation", errorObj);
        let errorMessage = "Unable to retrieve your location";

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage =
              "Location access denied. Please enable location permissions.";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = "Location information is unavailable.";
            break;
          case error.TIMEOUT:
            errorMessage = "Location request timed out.";
            break;
        }

        reject(new Error(errorMessage));
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000, // 5 minutes
      },
    );
  });
}
